import React, { createContext, useContext, useEffect, useState } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';

// True while the shell is still hydrating the browser's data layer. Every widget
// stays under its skeleton until then, so the hydration lag and the page remount
// that follows it happen out of sight (BD-1196).
export const HydratingContext = createContext(false);

const column = { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 };

// A query that answers within this long (most pre-aggregated widgets) never shows
// the skeleton, so a quick filter change doesn't flash. Hydration and page-level
// loads still cover straight away.
export const SKELETON_DELAY_MS = 300;

// Loading wins over failure: a retry in flight shows the skeleton, not the error.
export function queryStatus(queries) {
    const qs = queries.filter(Boolean);
    const fetching = qs.some((q) => q.isFetching);
    return { fetching, failed: fetching ? [] : qs.filter((q) => q.isError) };
}

function useAfterDelay(on, ms) {
    const [elapsed, setElapsed] = useState(false);
    useEffect(() => {
        setElapsed(false);
        if (!on) return undefined;
        const timer = setTimeout(() => setElapsed(true), ms);
        return () => clearTimeout(timer);
    }, [on, ms]);
    return on && elapsed;
}

// Skeleton straight away for hydration and page-level loads, after the delay for a
// query; an error only once nothing is loading.
function useWidgetStatus(queries, pending) {
    const hydrating = useContext(HydratingContext);
    const { fetching, failed } = queryStatus(queries);
    const slow = useAfterDelay(fetching, SKELETON_DELAY_MS);
    const loading = Boolean(pending || hydrating || slow);
    return { loading, failed: loading ? [] : failed };
}

const LoadingCover = () => (
    <Skeleton
        role="status"
        aria-label="Loading"
        variant="rounded"
        animation="wave"
        sx={{ position: 'absolute', inset: 0, height: 'auto', transform: 'none', '&::after': { animationDuration: '2.4s' } }}
    />
);

const ErrorCover = ({ failed }) => (
    <Box
        role="alert"
        sx={{
            position: 'absolute', inset: 0, borderRadius: 1, bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, p: 2, textAlign: 'center',
        }}
    >
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>This couldn't be loaded.</Typography>
        <Button size="small" variant="outlined" onClick={() => failed.forEach((q) => q.refetch())}>Retry</Button>
    </Box>
);

// BD-1196: while any of a widget's queries is fetching, a wave skeleton covers it.
// The widget stays mounted underneath (hidden), so the skeleton takes its normal
// size and nothing shifts when the data lands. A failed query hides the figures
// and offers Retry, rather than showing stale or partial numbers.
// `fill`: the widget stretches to fill a flex column (equal-height rows), so the
// wrapper has to pass that stretch through. `loading`: a load that isn't a query,
// e.g. the browser's first data hydration.
export default function WidgetState({ queries = [], loading: pending, fill, children }) {
    const { loading, failed } = useWidgetStatus(queries, pending);
    const showError = failed.length > 0;
    const covered = loading || showError;
    const layout = fill ? column : undefined;

    return (
        <Box sx={{ position: 'relative', ...layout }}>
            <Box aria-hidden={covered || undefined} style={{ visibility: covered ? 'hidden' : 'visible' }} sx={layout}>
                {children}
            </Box>
            {loading && <LoadingCover />}
            {showError && <ErrorCover failed={failed} />}
        </Box>
    );
}
